// GET /api/categories -- the taxonomy, per the M1 interface table:
// `{data: {taxonomy_version, categories: [...]}}`. Unlike every other
// collection endpoint in this codebase, `data` here is an object wrapping
// the array, not the array itself -- that is the wire shape as specified,
// not a drift to reconcile.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

// `kind` is nullable only for the `__taxonomy_marker__` sentinel row (design
// §3.1) -- every real leaf/parent the caller sees has one. `tax_line` and
// `slug` are internal methodology and MUST NOT be rendered to users (AGENTS.md
// §Data & UI honesty 4) -- callers pick `name` for display.
export const categoryKindSchema = z.enum(['expense', 'income', 'transfer']);

export const categorySchema = z.object({
  category_id: z.string(),
  parent_id: z.string().nullable(),
  slug: z.string(),
  name: z.string(),
  kind: categoryKindSchema.nullable(),
  is_system: z.boolean(),
  is_business: z.boolean(),
  tax_line: z.string().nullable(),
  sort_order: z.number(),
  is_custom: z.boolean(),
});

export const categoriesResponseSchema = z.object({
  taxonomy_version: z.string(),
  categories: z.array(categorySchema),
});

export type CategoryKind = z.infer<typeof categoryKindSchema>;
export type Category = z.infer<typeof categorySchema>;
export type CategoriesResponse = z.infer<typeof categoriesResponseSchema>;

// ENG-R1 deferrable ("render cost"): the taxonomy changes rarely (a new
// custom leaf, at most), so every mounted FeedRow/detail panel re-fetching
// it on each focus/mount was pure churn -- 5 minutes matches the app's
// other slow-changing reference data and still refreshes soon after a
// create. `useCreateCategory`'s own `onSuccess` invalidation (see
// `useCategoryMutations.ts`) still forces an immediate refetch after this
// user adds a type, so staleness never hides a just-created leaf.
const CATEGORIES_STALE_TIME_MS = 5 * 60 * 1000;

export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    staleTime: CATEGORIES_STALE_TIME_MS,
    queryFn: async () => {
      const response = await client.get('/api/categories');
      return parseEnvelope(categoriesResponseSchema, response.data).data;
    },
  });
}

/**
 * `useCategories` plus a memoised `category_id -> Category` map, so every
 * caller doing `categories.find((c) => c.category_id === id)` (FeedRow,
 * TransactionDetailPanel, CategoryChip callers) stops re-scanning the full
 * taxonomy array on every render -- ENG-R1 deferrable ("render cost": "a
 * memoised id->category map"). The map is recomputed only when the
 * underlying `categories` array reference changes (a real refetch), never
 * on an unrelated re-render.
 */
export function useCategoriesMap() {
  const query = useCategories();
  const categories = query.data?.categories;
  const byId = useMemo(() => {
    const map = new Map<string, Category>();
    if (categories) {
      for (const category of categories) map.set(category.category_id, category);
    }
    return map;
  }, [categories]);
  return { ...query, byId };
}
